import React, { useEffect, useState } from 'react';
import { AspectRatio } from '@mantine/core';
import {
  DEFAULT_MEDIA_ASPECT_RATIO,
  mediaFitVars,
  usesIntrinsicContainLayout,
} from './mediaFit';
import { useResolvedRatio } from './useMediaAspectRatio';
import {
  MEDIA_KINDS,
  normalizeMediaSource,
  resolveBackgroundPlayback,
  resolveInlinePlayback,
} from './mediaSource';
import { MediaEmbed } from './MediaEmbed.jsx';
import { MediaVideo } from './MediaVideo';
import classes from './MediaFrame.module.css';

/**
 * LE composant vidéo du webviewer — autonome, même signature qu'ImageFrame
 * plus les extras playback. Un VideoFrame ne rend JAMAIS une image : un src
 * fichier est toujours traité en vidéo ; un lien YouTube/Vimeo part dans
 * MediaEmbed (seul détenteur de l'iframe), le reste dans MediaVideo (seul
 * détenteur du <video>).
 * - asBackground : élément nu qui remplit un parent positionné, lecture
 *   auto muette en boucle (fond Hero/Stack)
 * - contenir + ratio auto : hauteur naturelle (intrinsic)
 * - défaut : cadre AspectRatio verrouillé
 */
export function VideoFrame({
  src = '',
  alt = '',
  mediaFit = 'cover',
  mediaAspectRatio = 'auto',
  fallbackRatio = DEFAULT_MEDIA_ASPECT_RATIO,
  radius = 0,
  className = '',
  style,
  isEditing = false,
  asBackground = false,
  onError,
  onPlayerState,
  stopOnUnmount = false,
  /* extras vidéo */
  playback = null,
  playbackOverride = null,
  inlinePlayback = false,
  poster = undefined,
}) {
  const input = playback || poster ? { src, playback, poster } : src;
  let resolved = normalizeMediaSource(input);
  if (resolved.kind === MEDIA_KINDS.image && resolved.src) {
    resolved = { ...resolved, kind: MEDIA_KINDS.video };
  }
  const ratio = useResolvedRatio(input, mediaAspectRatio, fallbackRatio);

  // Vidéo fichier en erreur : masquée au viewer, visible en édition (pour
  // corriger). Reset si la source change. Les embeds (iframe YouTube/Vimeo)
  // ne signalent pas leurs erreurs — non couverts.
  const [broken, setBroken] = useState(false);
  const resolvedSrc = resolved.src;
  useEffect(() => {
    setBroken(false);
  }, [resolvedSrc]);

  if (!resolved.src) return null;
  if (broken && !isEditing && resolved.kind !== MEDIA_KINDS.embed) return null;

  if (resolved.kind === MEDIA_KINDS.embed) {
    const embedMedia = playbackOverride
      ? { ...resolved, playback: { ...resolved.playback, ...playbackOverride } }
      : resolved;
    return (
      <MediaEmbed
        media={embedMedia}
        ratio={ratio}
        mediaFit={mediaFit}
        fill={asBackground}
        title={alt || 'Vidéo intégrée'}
        isEditing={isEditing}
        className={asBackground ? `${classes.mediaFill} ${className}`.trim() : className}
        style={style}
      />
    );
  }

  const resolvedPlayback = playbackOverride || (asBackground
    ? resolveBackgroundPlayback(resolved)
    : resolveInlinePlayback(resolved, { isEditing }));
  const intrinsic = usesIntrinsicContainLayout(mediaFit, mediaAspectRatio, {
    asBackground: asBackground || inlinePlayback,
  });

  const video = (
    <MediaVideo
      src={resolved.src}
      onPlayerState={onPlayerState}
      stopOnUnmount={stopOnUnmount}
      poster={resolved.poster}
      playback={resolvedPlayback}
      asBackground={asBackground}
      mediaFit={mediaFit}
      isEditing={isEditing}
      alt={alt}
      radius={radius}
      intrinsicSize={intrinsic}
      /* MediaVideo pose lui-même media/mediaIntrinsic — ne pas doubler */
      className={asBackground ? `${classes.mediaFill} ${className}`.trim() : ''}
      onError={(e) => {
        setBroken(true);
        if (typeof onError === 'function') onError(e);
      }}
    />
  );

  if (asBackground) return video;

  if (intrinsic) {
    return (
      <div className={`${classes.frame} ${classes.frameIntrinsic} ${className}`.trim()} style={style}>
        {video}
      </div>
    );
  }
  return (
    <div className={`${classes.frame} ${className}`.trim()} style={{ ...mediaFitVars(mediaFit), ...style }}>
      <AspectRatio ratio={ratio}>{video}</AspectRatio>
    </div>
  );
}
